import { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, Lock, BarChart2, Settings2, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { readCookieConsent, saveCookieConsent } from "@/lib/cookie-consent";

const CATEGORIES = [
  {
    id: "essential",
    icon: Lock,
    iconColor: "#4A3A8A",
    iconBg: "#f0eeff",
    label: "Necesarias",
    sublabel: "Siempre activas",
    desc: "Sesión autenticada, protección CSRF y tu elección de consentimiento. Son imprescindibles para que el sitio funcione.",
    fixed: true,
  },
  {
    id: "analytics",
    icon: BarChart2,
    iconColor: "#b45309",
    iconBg: "#fef9ee",
    label: "Analíticas",
    sublabel: "Opcional · Google Analytics",
    desc: "Nos ayudan a entender cómo se usa el sitio (páginas vistas, tiempo de visita) de forma agregada y anónima.",
    fixed: false,
    key: "analytics",
  },
  {
    id: "functional",
    icon: Settings2,
    iconColor: "#1565c0",
    iconBg: "#eff6ff",
    label: "Funcionales",
    sublabel: "Opcional · Preferencias",
    desc: "Guardan tus preferencias de idioma, tema o densidad de la interfaz para que no tengas que configurarlos cada vez.",
    fixed: false,
    key: "functional",
  },
];

function CategoryCard({ category, value, onChange }) {
  const { icon: Icon, iconColor, iconBg, label, sublabel, desc, fixed } = category;
  const active = fixed || value;

  return (
    <div
      className={cn(
        "relative flex flex-col items-center rounded-2xl border-2 p-5 text-center transition-all duration-150",
        fixed
          ? "border-violet-light/70 bg-[#faf9ff]"
          : active
          ? "border-violet-fuerte/50 bg-[#f4f1ff]"
          : "border-violet-light/60 bg-white hover:border-violet-fuerte/30"
      )}
    >
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
        style={{ background: iconBg }}
        aria-hidden
      >
        <Icon className="h-5 w-5" style={{ color: iconColor }} strokeWidth={1.75} />
      </div>

      <div className="mt-3 flex min-h-[28px] w-full items-center justify-center">
        {fixed ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-violet-fuerte/10 px-2.5 py-1 text-[11px] font-bold text-violet-fuerte">
            <CheckCircle2 className="h-3 w-3 shrink-0" aria-hidden />
            Activa
          </span>
        ) : (
          <Switch
            checked={value}
            onCheckedChange={onChange}
            className="shrink-0 data-[state=checked]:bg-violet-fuerte data-[state=unchecked]:bg-slate-200"
            aria-label={`${active ? "Desactivar" : "Activar"} cookies ${label.toLowerCase()}`}
          />
        )}
      </div>

      <div className="mt-4 w-full flex-1">
        <p className="font-display text-[15px] font-bold leading-snug text-ink">{label}</p>
        <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide" style={{ color: iconColor }}>
          {sublabel}
        </p>
        <p className="mt-2.5 text-[12.5px] leading-relaxed text-ink-3">{desc}</p>
      </div>
    </div>
  );
}

export default function CookieSettingsModal({ open, onOpenChange }) {
  const [analytics, setAnalytics] = useState(false);
  const [functional, setFunctional] = useState(false);

  useEffect(() => {
    if (!open) return;
    const c = readCookieConsent();
    setAnalytics(c.analytics);
    setFunctional(c.functional);
  }, [open]);

  const values = { analytics, functional };
  const setters = { analytics: setAnalytics, functional: setFunctional };

  const handleSave = () => {
    saveCookieConsent({ analytics, functional });
    onOpenChange(false);
  };

  const acceptAll = () => {
    saveCookieConsent({ analytics: true, functional: true });
    onOpenChange(false);
  };

  const rejectOptional = () => {
    saveCookieConsent({ analytics: false, functional: false });
    onOpenChange(false);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-[130] bg-black/50 backdrop-blur-[3px]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />

        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-[130] w-[min(calc(100vw-1.5rem),44rem)]",
            "max-h-[min(90vh,680px)] -translate-x-1/2 -translate-y-1/2",
            "overflow-hidden rounded-2xl border border-violet-light/80 bg-white",
            "shadow-[0_32px_80px_-20px_rgba(26,17,53,0.4)] duration-200",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          {/* Top accent bar */}
          <div
            className="h-1 w-full"
            style={{ background: "linear-gradient(90deg, transparent, #C9A227 40%, #4A3A8A 70%, transparent)" }}
            aria-hidden
          />

          <div className="overflow-y-auto overscroll-contain" style={{ maxHeight: "min(calc(90vh - 0.25rem), 676px)" }}>
            {/* ── Header ─────────────────────────────────────── */}
            <div className="flex items-start justify-between gap-4 px-6 pb-0 pt-5 sm:px-8 sm:pt-6">
              <div className="min-w-0">
                <DialogPrimitive.Title className="font-display text-xl font-bold leading-tight text-ink sm:text-2xl">
                  Configuración de cookies
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="mt-1.5 text-[13.5px] leading-relaxed text-ink-2">
                  Elige qué cookies opcionales permites. Las necesarias no pueden desactivarse.{" "}
                  <Link
                    to="/cookies"
                    className="font-semibold text-violet-fuerte underline decoration-violet-fuerte/30 underline-offset-2 hover:text-violet-oscuro"
                    onClick={() => onOpenChange(false)}
                  >
                    Ver política de cookies
                  </Link>
                </DialogPrimitive.Description>
              </div>
              <DialogPrimitive.Close
                type="button"
                className="mt-0.5 shrink-0 rounded-xl p-1.5 text-ink-3 transition hover:bg-violet-light/40 hover:text-ink focus:outline-none focus:ring-2 focus:ring-violet-fuerte focus:ring-offset-2"
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </DialogPrimitive.Close>
            </div>

            {/* ── Cookie categories ──────────────────────────── */}
            <div className="grid grid-cols-1 gap-3 px-6 pt-5 sm:grid-cols-3 sm:px-8">
              {CATEGORIES.map((cat) => (
                <CategoryCard
                  key={cat.id}
                  category={cat}
                  value={cat.fixed ? true : values[cat.key]}
                  onChange={cat.fixed ? undefined : setters[cat.key]}
                />
              ))}
            </div>

            {/* ── Actions ────────────────────────────────────── */}
            <div className="flex flex-col gap-2 border-t border-violet-light/60 px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8 sm:py-6">
              <button
                type="button"
                onClick={rejectOptional}
                className="inline-flex h-11 items-center justify-center rounded-xl border border-violet-light/80 bg-[#FAFAFE] px-5 text-[14px] font-semibold text-ink-2 transition-colors hover:border-violet-fuerte/30 hover:bg-white hover:text-ink"
              >
                Solo necesarias
              </button>

              <div className="flex gap-2 sm:items-center">
                <button
                  type="button"
                  onClick={handleSave}
                  className="inline-flex h-11 flex-1 items-center justify-center rounded-xl border border-violet-fuerte/40 bg-white px-5 text-[14px] font-semibold text-violet-fuerte transition-colors hover:bg-[#f4f1ff] sm:flex-none"
                >
                  Guardar selección
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="inline-flex h-11 flex-1 items-center justify-center rounded-xl px-5 text-[14px] font-bold text-ink shadow-[0_4px_14px_-4px_rgba(201,162,39,0.55)] transition-[filter] hover:brightness-105 sm:flex-none"
                  style={{ background: "linear-gradient(160deg, #f0d96a 0%, #C9A227 60%, #a07d18 100%)" }}
                >
                  Aceptar todas
                </button>
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
